import { Button, Collapse, Input, Skeleton } from 'antd'
import { InferenceButton } from 'features/documentUpload/inference/InferenceButton'
import {
  EnrichedOntologyFieldDefinition,
  OntologyV2,
  OntologyValueType,
} from 'features/dynamicOntology'
import { LeafActionsParams, RecursivePropertyView } from 'features/objectViews'
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components'

import { DocumentOntologySelector } from './DocOntologySelector'
import {
  blankDocumentSectionFields,
  DocumentSectionFields,
  ROLODEX_DOC_FIELDS_TYPE,
} from './documentSectionFieldsApi'
import { DocumentOntology } from './useDocumentOntologyAdditions'

export type DocumentSectionFieldsSetter = (
  value: SetStateAction<DocumentSectionFields | undefined>,
  options?: { preserveTitle?: boolean }
) => void

export interface DocumentSectionFieldsViewProps {
  documentSectionFields: DocumentSectionFields
  setDocumentSectionFields: DocumentSectionFieldsSetter
  documentOntologies: ReadonlyArray<DocumentOntology>
  isLoading: boolean
  documentId: string
}

function resolveSetState<T>(action: SetStateAction<T>, prevValue: T): T {
  return typeof action === 'function'
    ? (action as (prevState: T) => T)(prevValue)
    : action
}

function useOntologyFields(
  ontology: OntologyV2 | undefined
): Record<string, [string, EnrichedOntologyFieldDefinition]> | undefined {
  return useMemo(() => {
    if (!ontology) {
      return undefined
    }

    const docFieldsType = ontology.types[ROLODEX_DOC_FIELDS_TYPE]
    if (!docFieldsType) {
      return {}
    }

    const fieldsEntries = Object.entries(docFieldsType.fields)
      .filter(([key]) => key.startsWith('fields.')) // Do we need this filter anymore with the ontology v2?
      .map(([key, fieldDef]) => {
        const enrichedFieldDef: EnrichedOntologyFieldDefinition = {
          ...fieldDef,
          key,
          entryType: ROLODEX_DOC_FIELDS_TYPE,
          keyOrField: 'key',
        }
        return [key, enrichedFieldDef] as [
          string,
          EnrichedOntologyFieldDefinition,
        ]
      })
      .map(([key, fieldDef]) => {
        const split = key.split('.')
        if (split.length < 1) {
          console.warn(`Invalid ontology field key: ${key}`)
          return undefined
        }
        return [split.slice(1).join('.'), [key, fieldDef]]
      })
      .filter(
        (entry): entry is [string, [string, EnrichedOntologyFieldDefinition]] =>
          entry !== undefined
      )

    return Object.fromEntries(fieldsEntries)
  }, [ontology])
}

const RawDocumentSectionFieldsView: React.FC<
  DocumentSectionFieldsViewProps
> = ({
  documentSectionFields,
  documentOntologies,
  setDocumentSectionFields,
  isLoading,
  documentId,
}) => {
  const { title } = documentSectionFields

  const selectedDocumentOntology = useMemo(
    () =>
      documentOntologies.find(
        (ontology) => ontology.id === documentSectionFields.documentOntologyId
      ),
    [documentSectionFields.documentOntologyId, documentOntologies]
  )

  const setTitle = useCallback(
    (val: SetStateAction<string>) => {
      const newTitle = typeof val === 'function' ? val(title) : val
      setDocumentSectionFields(
        (prev) => {
          return {
            ...(prev ?? blankDocumentSectionFields()),
            title: newTitle,
          }
        },
        { preserveTitle: true }
      )
    },
    [setDocumentSectionFields, title]
  )

  const setSelectedDocumentOntologyId = useCallback(
    (val: SetStateAction<string | null>) => {
      const newSelectedDocumentOntology =
        typeof val === 'function'
          ? val(selectedDocumentOntology?.id ?? null)
          : val
      const ontology = documentOntologies.find(
        (ontology) => ontology.id === newSelectedDocumentOntology
      )
      setDocumentSectionFields((prev) => ({
        ...(prev ?? blankDocumentSectionFields()),
        documentOntologyId: newSelectedDocumentOntology ?? undefined,
        title:
          !prev?.title || prev.title.startsWith('Untitled')
            ? `Untitled ${ontology?.name ?? ''} Section`
            : prev.title,
      }))
    },
    [selectedDocumentOntology?.id, documentOntologies, setDocumentSectionFields]
  )

  const setFields = useCallback(
    (value: SetStateAction<OntologyValueType | undefined>) => {
      setDocumentSectionFields((prev) => {
        const prevFields = prev ?? blankDocumentSectionFields()
        const nextFields = resolveSetState(
          value,
          prevFields.fields as OntologyValueType | undefined
        )

        return {
          ...prevFields,
          fields: (nextFields ?? {}) as Record<string, unknown>,
        }
      })
    },
    [setDocumentSectionFields]
  )

  return (
    <FieldViewPanelContainer
      title={title}
      setTitle={setTitle}
      isLoading={isLoading}
      documentOntologies={documentOntologies}
      setSelectedDocumentOntologyId={setSelectedDocumentOntologyId}
      selectedDocumentOntology={selectedDocumentOntology}
      delete={() => setDocumentSectionFields(undefined)}>
      <DocumentFieldsContentView
        fields={documentSectionFields.fields}
        selectedDocumentOntology={selectedDocumentOntology}
        setFields={setFields}
        documentId={documentId}
      />
    </FieldViewPanelContainer>
  )
}
export const DocumentSectionFieldsView = React.memo(
  RawDocumentSectionFieldsView
)

export const DocumentFieldsContentView: React.FC<{
  fields: Record<string, unknown>
  selectedDocumentOntology: DocumentOntology | undefined
  setFields: Dispatch<SetStateAction<OntologyValueType | undefined>>
  documentId: string
}> = ({ fields, selectedDocumentOntology, setFields, documentId }) => {
  const ontology = selectedDocumentOntology?.ontology ?? undefined
  const ontologyFields = useOntologyFields(ontology)

  const virtualObject = useMemo<OntologyValueType>(() => {
    return ontologyFields
      ? (Object.fromEntries(
          Object.keys(ontologyFields).map((key) => [key, fields[key]])
        ) as OntologyValueType)
      : (fields as OntologyValueType)
  }, [ontologyFields, fields])

  const leafActions = useCallback(
    ({ fieldDef, setObject }: LeafActionsParams) => {
      return setObject && documentId ? (
        <InferenceButton
          fieldDef={fieldDef}
          setValue={setObject}
          documentId={documentId}
        />
      ) : undefined
    },
    [documentId]
  )
  return (
    <RecursivePropertyView
      label={undefined}
      object={virtualObject}
      setObject={setFields}
      ontologyFields={ontologyFields}
      leafActions={leafActions}
    />
  )
}

const StyledCollapse = styled(Collapse)`
  margin-top: 1rem;

  .ant-collapse-item {
    border: none;
  }

  .ant-collapse-header {
    display: flex !important;
    align-items: center !important;
    min-height: 4rem;
    padding: 0 1rem !important;
    background: ${({ theme }) => theme.forecast.background3};
  }

  .ant-collapse-content {
    border-top: none;
  }

  .ant-collapse-content-box {
    padding: 1rem;
  }
`

const HeaderContent = styled.div`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  width: 100%;
  gap: 1rem;
  min-width: 0;
`

const HeaderActionsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 0 0 40%;
  min-width: 0;
  overflow: hidden;
`

const SelectorWrapper = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
`

const TitleInput = styled(Input)`
  flex: 0 0 60%;
  min-width: 0;
`

const DeleteButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border-radius: 50%;
  background: transparent;
  border-color: transparent;
  color: ${({ theme }) => theme.forecast.defaultText};
  cursor: pointer;
  font-weight: 600;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.forecast.primary};
    background: transparent;
    border-color: transparent;
  }
`

const SkeletonContainer = styled.div`
  margin-top: 1rem;
`

const FieldViewPanelContainer: React.FC<{
  children: ReactNode
  title: string
  setTitle: Dispatch<SetStateAction<string>>
  isLoading: boolean
  setSelectedDocumentOntologyId: Dispatch<SetStateAction<string | null>>
  selectedDocumentOntology: DocumentOntology | undefined
  documentOntologies: ReadonlyArray<DocumentOntology>
  delete: () => void
}> = ({
  children,
  title,
  setTitle,
  setSelectedDocumentOntologyId,
  selectedDocumentOntology,
  isLoading,
  documentOntologies,
  delete: deleteFields,
}) => {
  if (isLoading) {
    return (
      <SkeletonContainer>
        <Skeleton active paragraph={{ rows: 4 }} title />
      </SkeletonContainer>
    )
  }

  return (
    <StyledCollapse
      defaultActiveKey={[]}
      items={[
        {
          key: 'fields',
          label: (
            <HeaderContent>
              <TitleInput
                value={title}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => {
                  setTitle(e.target.value)
                  e.stopPropagation()
                  e.preventDefault()
                }}
              />
              <HeaderActionsContainer onClick={(e) => e.stopPropagation()}>
                <SelectorWrapper>
                  <DocumentOntologySelector
                    label='Type'
                    selectedOntologyId={selectedDocumentOntology?.id ?? null}
                    setSelectedOntologyId={setSelectedDocumentOntologyId}
                    documentOntologies={documentOntologies}
                    isLoading={isLoading}
                  />
                </SelectorWrapper>
                <DeleteButton shape='circle' onClick={() => deleteFields()}>
                  X
                </DeleteButton>
              </HeaderActionsContainer>
            </HeaderContent>
          ),
          children,
        },
      ]}
    />
  )
}
